'use no memo';

import { useQuery } from '@tanstack/react-query';
import { ResultHelpers } from '@treneo/models';
import { getPaginationParams, type PaginationRequest } from '@treneo/shared/utils';

import { organizationsApi } from '../api';
import {
  MANAGED_ORGANIZATION_USERS_QUERY_KEY,
  MY_MANAGED_ORGANIZATIONS_QUERY_KEY,
  MY_PENDING_TRAINER_INVITATIONS_QUERY_KEY,
  ORGANIZATION_INVITATIONS_QUERY_KEY,
  ORGANIZATION_MEMBERSHIP_HISTORY_QUERY_KEY,
  ORGANIZATION_TRAINERS_BY_DADESKTOP_QUERY_KEY,
  ORGANIZATION_TRAINERS_QUERY_KEY,
  ORGANIZATION_USERS_QUERY_KEY,
  SYSTEM_INVITATIONS_QUERY_KEY,
  TRAINER_INVITATION_BY_TOKEN_QUERY_KEY
} from '../organizations-query-keys';

export {
  MANAGED_ORGANIZATION_USERS_QUERY_KEY,
  MY_MANAGED_ORGANIZATIONS_QUERY_KEY,
  MY_PENDING_TRAINER_INVITATIONS_QUERY_KEY,
  ORGANIZATION_INVITATIONS_QUERY_KEY,
  ORGANIZATION_MEMBERSHIP_HISTORY_QUERY_KEY,
  ORGANIZATION_TRAINERS_BY_DADESKTOP_QUERY_KEY,
  ORGANIZATION_TRAINERS_QUERY_KEY,
  ORGANIZATION_USERS_QUERY_KEY,
  ORGANIZATIONS_QUERY_KEY,
  SYSTEM_INVITATIONS_QUERY_KEY,
  TRAINER_INVITATION_BY_TOKEN_QUERY_KEY
} from '../organizations-query-keys';

export const getOrganizationTrainersQueryOptions = (organizationId: string) => ({
  queryKey: [ORGANIZATION_TRAINERS_QUERY_KEY, organizationId] as const,
  queryFn: async () =>
    ResultHelpers.unwrap(
      await organizationsApi.getOrganizationTrainers(organizationId),
      'organizations.errors.getOrganizationTrainersFailed'
    )
});

export const useOrganizationTrainersQuery = (
  organizationId: string,
  options?: { enabled?: boolean }
) => {
  const queryOptions = getOrganizationTrainersQueryOptions(organizationId);
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    enabled: (options?.enabled !== false && !!organizationId) || false
  });
};

export const getOrganizationTrainersByDaDesktopIdQueryOptions = (daDesktopId: number) => ({
  queryKey: [ORGANIZATION_TRAINERS_BY_DADESKTOP_QUERY_KEY, daDesktopId] as const,
  queryFn: async () =>
    ResultHelpers.unwrap(
      await organizationsApi.getOrganizationTrainersByDaDesktopId(daDesktopId),
      'organizations.errors.getOrganizationTrainersByDaDesktopIdFailed'
    )
});

export const useOrganizationTrainersByDaDesktopIdQuery = (
  daDesktopId: number,
  options?: { enabled?: boolean }
) => {
  const queryOptions = getOrganizationTrainersByDaDesktopIdQueryOptions(daDesktopId);
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    enabled: (options?.enabled !== false && daDesktopId != null && daDesktopId > 0) || false,
    refetchOnMount: 'always'
  });
};

export const getOrganizationInvitationsQueryOptions = (
  organizationId: string,
  request?: PaginationRequest
) => {
  const { page, pageSize, pageApi } = getPaginationParams(request);
  return {
    queryKey: [ORGANIZATION_INVITATIONS_QUERY_KEY, organizationId, { page, pageSize }] as const,
    queryFn: async () =>
      ResultHelpers.unwrap(
        await organizationsApi.getOrganizationInvitations(organizationId, {
          page: pageApi,
          pageSize
        }),
        'organizations.errors.getOrganizationInvitationsFailed'
      )
  };
};

export const useOrganizationInvitationsQuery = (
  organizationId: string,
  options?: { request?: PaginationRequest; enabled?: boolean }
) => {
  const queryOptions = getOrganizationInvitationsQueryOptions(organizationId, options?.request);
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    enabled: (options?.enabled !== false && !!organizationId) || false
  });
};

export const getTrainerInvitationByTokenQueryOptions = (token: string) => ({
  queryKey: [TRAINER_INVITATION_BY_TOKEN_QUERY_KEY, token] as const,
  queryFn: async () =>
    ResultHelpers.unwrap(
      await organizationsApi.getTrainerInvitationByToken(token),
      'organizations.errors.getTrainerInvitationFailed'
    )
});

export const useTrainerInvitationByTokenQuery = (
  token: string,
  options?: { enabled?: boolean }
) => {
  const queryOptions = getTrainerInvitationByTokenQueryOptions(token);
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    enabled: (options?.enabled !== false && !!token) || false
  });
};

export const getMyPendingTrainerInvitationsQueryOptions = () => ({
  queryKey: [MY_PENDING_TRAINER_INVITATIONS_QUERY_KEY] as const,
  queryFn: async () =>
    ResultHelpers.unwrap(
      await organizationsApi.getMyPendingTrainerInvitations(),
      'organizations.errors.getMyPendingTrainerInvitationsFailed'
    )
});

export const useMyPendingTrainerInvitationsQuery = (options?: { enabled?: boolean }) => {
  const queryOptions = getMyPendingTrainerInvitationsQueryOptions();
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    enabled: options?.enabled !== false
  });
};

export const getOrganizationUsersQueryOptions = (
  organizationId: string,
  request?: PaginationRequest
) => {
  const { page, pageSize, pageApi } = getPaginationParams(request);
  return {
    queryKey: [ORGANIZATION_USERS_QUERY_KEY, organizationId, { page, pageSize }] as const,
    queryFn: async () =>
      ResultHelpers.unwrap(
        await organizationsApi.getOrganizationUsers(organizationId, {
          page: pageApi,
          pageSize
        }),
        'organizations.errors.getOrganizationUsersFailed'
      )
  };
};

export const useOrganizationUsersQuery = (
  organizationId: string,
  options?: { request?: PaginationRequest; enabled?: boolean }
) => {
  const queryOptions = getOrganizationUsersQueryOptions(organizationId, options?.request);
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    enabled: (options?.enabled !== false && !!organizationId) || false
  });
};

export const getOrganizationMembershipHistoryQueryOptions = (
  organizationId: string,
  request?: PaginationRequest
) => {
  const { page, pageSize, pageApi } = getPaginationParams(request);
  return {
    queryKey: [
      ORGANIZATION_MEMBERSHIP_HISTORY_QUERY_KEY,
      organizationId,
      { page, pageSize }
    ] as const,
    queryFn: async () =>
      ResultHelpers.unwrap(
        await organizationsApi.getOrganizationMembershipHistory(organizationId, {
          page: pageApi,
          pageSize
        }),
        'organizations.errors.getOrganizationMembershipHistoryFailed'
      )
  };
};

export const useOrganizationMembershipHistoryQuery = (
  organizationId: string,
  opts?: { request?: PaginationRequest; enabled?: boolean }
) => {
  const queryOptions = getOrganizationMembershipHistoryQueryOptions(organizationId, opts?.request);
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    enabled: (opts?.enabled !== false && !!organizationId) || false
  });
};

export const getMyManagedOrganizationsQueryOptions = (request?: PaginationRequest) => {
  const { page, pageSize, pageApi } = getPaginationParams(request);
  return {
    queryKey: [MY_MANAGED_ORGANIZATIONS_QUERY_KEY, { page, pageSize }] as const,
    queryFn: async () =>
      ResultHelpers.unwrap(
        await organizationsApi.getMyManagedOrganizations({ page: pageApi, pageSize }),
        'organizations.errors.getMyManagedOrganizationsFailed'
      )
  };
};

export const useMyManagedOrganizationsQuery = (request?: PaginationRequest) => {
  const queryOptions = getMyManagedOrganizationsQueryOptions(request);
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn
  });
};

export const getManagedOrganizationUsersQueryOptions = (request?: PaginationRequest) => {
  const { page, pageSize, pageApi } = getPaginationParams(request);
  return {
    queryKey: [MANAGED_ORGANIZATION_USERS_QUERY_KEY, { page, pageSize }] as const,
    queryFn: async () =>
      ResultHelpers.unwrap(
        await organizationsApi.getManagedOrganizationUsers({
          page: pageApi,
          pageSize
        }),
        'organizations.errors.getManagedOrganizationUsersFailed'
      )
  };
};

export const useManagedOrganizationUsersQuery = (request?: PaginationRequest) => {
  const queryOptions = getManagedOrganizationUsersQueryOptions(request);
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn
  });
};

export const getSystemInvitationsQueryOptions = (request?: PaginationRequest) => {
  const { page, pageSize, pageApi } = getPaginationParams(request);
  return {
    queryKey: [SYSTEM_INVITATIONS_QUERY_KEY, { page, pageSize }] as const,
    queryFn: async () =>
      ResultHelpers.unwrap(
        await organizationsApi.getSystemInvitations({ page: pageApi, pageSize }),
        'organizations.errors.getSystemInvitationsFailed'
      )
  };
};

export const useSystemInvitationsQuery = (
  request?: PaginationRequest,
  options?: { enabled?: boolean }
) => {
  const queryOptions = getSystemInvitationsQueryOptions(request);
  return useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    enabled: options?.enabled !== false
  });
};
